import type { DiscoverItem, DiscoverSegment } from "../types";
import { CATEGORY_STYLE } from "../components/map/category-style";

export type BucketId =
  | "things-to-do"
  | "restaurants"
  | "stays"
  | "locations"
  | "spas"
  | "golf"
  | "nightlife"
  | "beach-clubs";

export interface Bucket {
  readonly id: BucketId;
  readonly labelKey: string;
  readonly icon: string;
  /** Category colour, shared with the map pins / card chips via CATEGORY_STYLE. */
  readonly color: string;
  readonly match: (i: DiscoverItem) => boolean;
}

/** Each browse bucket maps to a DiscoverSegment so it can borrow that segment's colour. */
const BUCKET_SEGMENT: Record<BucketId, DiscoverSegment> = {
  "things-to-do": "activities",
  restaurants: "restaurants",
  stays: "stays",
  locations: "locations",
  spas: "spas",
  golf: "golf",
  nightlife: "nightlife",
  "beach-clubs": "beach-clubs",
};

/** Resolve a bucket's colour from the shared per-segment map (matches map pins). */
export function bucketColor(id: BucketId): string {
  return CATEGORY_STYLE[BUCKET_SEGMENT[id]].color;
}

export const BUCKETS: readonly Bucket[] = [
  {
    id: "things-to-do",
    labelKey: "discover.bucket.things_to_do",
    icon: "ri-compass-3-line",
    color: bucketColor("things-to-do"),
    // Segment-based only. Do NOT match on the "things-to-do" tab — inferTabs
    // adds that tab to almost every item, which made this bucket match all of
    // them (so filtering by Activities looked like a no-op).
    match: (i) => i.segment === "activities" || i.segment === "bookable",
  },
  {
    id: "restaurants",
    labelKey: "discover.bucket.restaurants",
    icon: "ri-restaurant-line",
    color: bucketColor("restaurants"),
    match: (i) => i.segment === "restaurants",
  },
  {
    id: "stays",
    labelKey: "discover.bucket.stays",
    icon: "ri-hotel-bed-line",
    color: bucketColor("stays"),
    match: (i) => i.segment === "stays",
  },
  {
    id: "locations",
    labelKey: "discover.bucket.locations",
    icon: "ri-landscape-line",
    color: bucketColor("locations"),
    // Segment-based only — the "attractions" tab is also on activities/bookable
    // items, which would over-match here.
    match: (i) => i.segment === "locations",
  },
  {
    id: "spas",
    labelKey: "discover.bucket.spas",
    icon: "ri-leaf-line",
    color: bucketColor("spas"),
    match: (i) => i.segment === "spas",
  },
  {
    id: "golf",
    labelKey: "discover.bucket.golf",
    icon: "ri-golf-ball-line",
    color: bucketColor("golf"),
    match: (i) => i.segment === "golf",
  },
  {
    id: "nightlife",
    labelKey: "discover.bucket.nightlife",
    icon: "ri-goblet-line",
    color: bucketColor("nightlife"),
    match: (i) => i.segment === "nightlife",
  },
  {
    // Day-pass beach and pool clubs. Distinct from `locations`, which still
    // holds the free public beaches (Mehdia, Temara, Anza, Martil).
    id: "beach-clubs",
    labelKey: "discover.bucket.beach_clubs",
    icon: "ri-umbrella-line",
    color: bucketColor("beach-clubs"),
    match: (i) => i.segment === "beach-clubs",
  },
];
